import { addBoxThunk, addNavItem, reorderNavItem } from "../../../common/actions";
import { PAGE_TYPES } from "../../../common/constants";
import { createBox } from "../../../common/common_tools";
import { addMotionSceneBox } from "../../utils/motionSceneBox";

const ROOT = 0;
const FPS = 30;

/** Every id below `id` in the scene tree (sections can nest). */
function descendantsOf(navItemsById, id) {
  const out = [];
  const walk = (nid) => {
    const children = (navItemsById[nid] && navItemsById[nid].children) || [];
    children.forEach((c) => {
      out.push(c);
      walk(c);
    });
  };
  walk(id);
  return out;
}

/**
 * Where the new scene goes.
 *
 * "start": first in the course. ADD_NAV_ITEM places the id at `position` in
 * the flat `navItemsIds` list but APPENDS it to the root's `children` (what
 * the scene list renders from), so a reorder of the root puts the tree in the
 * same order as the flat list.
 *
 * "after": right after the scene the user picked (`afterNavId`), as its
 * sibling — a scene inside a section stays in that section. ADD_NAV_ITEM's
 * own `navItemSelected` slot inserts it after that id among the parent's
 * children; a picked section is skipped as a whole (after its last
 * descendant in the flat list).
 */
function addIntroNav(dispatch, navId, ctx) {
  const { placement, navItemsIds = [], rootChildren = [], navItemsById = {}, afterNavId } = ctx;
  const picked = afterNavId && navItemsById[afterNavId];
  const after = placement === "after" && picked && afterNavId !== ROOT;

  if (after) {
    const parent = picked.parent != null ? picked.parent : ROOT;
    const block = [afterNavId, ...descendantsOf(navItemsById, afterNavId)];
    const lastFlat = Math.max(...block.map((id) => navItemsIds.indexOf(id)));
    const position = lastFlat >= 0 ? lastFlat + 1 : navItemsIds.length;
    dispatch(
      addNavItem(
        navId,
        ctx.sceneName,
        parent,
        PAGE_TYPES.SLIDE,
        position,
        "rgb(255,255,255)",
        0,
        true, // hideTitles — the intro paints every pixel
        true,
        null,
        afterNavId
      )
    );
    return;
  }

  dispatch(
    addNavItem(
      navId,
      ctx.sceneName,
      ROOT,
      PAGE_TYPES.SLIDE,
      0,
      "rgb(255,255,255)",
      0,
      true,
      true,
      null,
      null
    )
  );
  const others = (ids) => (ids || []).filter((id) => id !== navId);
  dispatch(
    reorderNavItem(
      navId,
      ROOT,
      ROOT,
      [navId, ...others(navItemsIds)],
      [navId, ...others(rootChildren)]
    )
  );
}

/**
 * A rendered mp4 / WebP as one full-bleed box: a video (EnrichedPlayer) or an
 * image (HotspotImages) — the same plugins the job tray uses for AI media,
 * fed the url the same way (on ids, so the plugin takes it as state.url).
 */
function addMediaBox(dispatch, result, navId, boxes) {
  const pluginName = result.kind === "mp4" ? "EnrichedPlayer" : "HotspotImages";
  createBox(
    {
      id: result.boxId,
      parent: navId,
      container: 0,
      page: navId,
      name: pluginName,
      position: { x: 0, y: 0, type: "absolute" },
      url: result.url,
      width: 100,
      height: 100,
      widthUnit: "%",
      heightUnit: "%",
      timeline: { startFrame: 0, endFrame: Math.round((result.durationSec || 0) * FPS) },
    },
    pluginName,
    true,
    (...args) => dispatch(addBoxThunk(...args)),
    boxes || {}
  );
}

/**
 * Insert a logo intro as a new scene.
 *
 * @param {Function} dispatch
 * @param {object} result - motion: { navId, boxId, box, toolbar };
 *   mp4 / webp: { navId, boxId, url, kind, durationSec }
 * @param {{ placement?: "start"|"after", navItemsIds, rootChildren, navItemsById,
 *   afterNavId, boxes, sceneName: string }} ctx
 * @returns {string} the new scene id
 */
export function insertLogoIntroScene(dispatch, result, ctx) {
  const navId = result.navId;
  addIntroNav(dispatch, navId, ctx);
  if (result.kind === "mp4" || result.kind === "webp") {
    addMediaBox(dispatch, result, navId, ctx.boxes);
  } else {
    addMotionSceneBox(dispatch, result, navId);
  }
  return navId;
}
